import { fromMarkdown } from 'mdast-util-from-markdown';
import { citationSegments, safeCitationUrl, type Citation } from './citations.js';

export type MessageNode =
  | string
  | {
      tag: 'p' | 'strong' | 'em' | 'ul' | 'ol' | 'li' | 'br' | 'code' | 'pre' | 'blockquote' | 'a';
      children: MessageNode[];
      href?: string;
      start?: number | undefined;
    };
type SourceNode = ReturnType<typeof fromMarkdown>['children'][number];
type ParseNode =
  | SourceNode
  | {
      type: string;
      value?: string;
      children?: ParseNode[];
      url?: string;
      alt?: string;
      identifier?: string;
      ordered?: boolean;
      start?: number | null;
      position?: { start: { offset?: number }; end: { offset?: number } };
    };

/** Produces only allowlisted nodes, never HTML. Provider offsets refer to the original Markdown. */
export function messageMarkdown(text: string, citations: Citation[] | null = []): MessageNode[] {
  if (text.length > 100_000) return [text];
  const tree = fromMarkdown(text);
  const definitions = new Map(
    tree.children.flatMap((node) =>
      node.type === 'definition' ? [[node.identifier.toUpperCase(), node.url] as const] : [],
    ),
  );
  const convert = (node: ParseNode, linked = false, depth = 0): MessageNode[] => {
    if (depth > 32) return [text.slice(node.position?.start.offset, node.position?.end.offset)];
    const children = () =>
      ('children' in node ? (node.children ?? []) : []).flatMap((child) =>
        convert(child, linked, depth + 1),
      );
    const wrap = (
      tag: Exclude<MessageNode, string>['tag'],
      content = children(),
    ): MessageNode[] => [{ tag, children: content }];
    switch (node.type) {
      case 'text': {
        const value = 'value' in node ? (node.value ?? '') : '';
        const start = node.position?.start.offset;
        const end = node.position?.end.offset;
        if (linked || start === undefined || end === undefined || text.slice(start, end) !== value)
          return [value];
        const ranges = (citations ?? [])
          .filter(
            (c) =>
              Number.isInteger(c.startIndex) &&
              Number.isInteger(c.endIndex) &&
              c.startIndex! >= 0 &&
              c.endIndex! <= text.length &&
              c.endIndex! > c.startIndex! &&
              c.endIndex! > start &&
              c.startIndex! < end,
          )
          .map((c) => ({
            ...c,
            startIndex: Math.max(c.startIndex!, start) - start,
            endIndex: Math.min(c.endIndex!, end) - start,
          }));
        return citationSegments(value, ranges)
          .filter((part) => part.text.length > 0)
          .map((part) =>
            part.url ? { tag: 'a', href: part.url, children: [part.text] } : part.text,
          );
      }
      case 'paragraph':
        return wrap('p');
      case 'strong':
        return wrap('strong');
      case 'emphasis':
        return wrap('em');
      case 'heading':
        return wrap('p', wrap('strong'));
      case 'blockquote':
        return wrap('blockquote');
      case 'list':
        return [
          {
            tag: 'ordered' in node && node.ordered ? 'ol' : 'ul',
            start: 'start' in node ? (node.start ?? undefined) : undefined,
            children: children(),
          },
        ];
      case 'listItem':
        return wrap('li');
      case 'break':
        return wrap('br', []);
      case 'inlineCode':
        return wrap('code', ['value' in node ? (node.value ?? '') : '']);
      case 'code':
        return wrap('pre', wrap('code', ['value' in node ? (node.value ?? '') : '']));
      case 'link':
      case 'linkReference': {
        const content = ('children' in node ? (node.children ?? []) : []).flatMap((child) =>
          convert(child, true, depth + 1),
        );
        const href = safeCitationUrl(
          'url' in node
            ? node.url
            : 'identifier' in node
              ? definitions.get(node.identifier?.toUpperCase() ?? '')
              : undefined,
        );
        return href && !linked ? [{ tag: 'a', href, children: content }] : content;
      }
      case 'image':
      case 'imageReference':
        return ['alt' in node ? (node.alt ?? '') : ''];
      case 'html':
        return ['value' in node ? (node.value ?? '') : ''];
      case 'definition':
        return [];
      default:
        return children();
    }
  };
  return tree.children.flatMap((node) => convert(node));
}

export const messageMarkdownStyles = `
.message-markdown{white-space:normal;overflow-wrap:anywhere;min-width:0;line-height:1.5}
.message-markdown p{white-space:pre-wrap;margin:0 0 .65em}
.message-markdown p:last-child{margin-bottom:0}
.message-markdown ul,.message-markdown ol{margin:.65em 0;padding-inline-start:1.5em}
.message-markdown ul{list-style:disc outside}
.message-markdown ol{list-style:decimal outside}
.message-markdown li{margin:.35em 0}
.message-markdown li>p{margin:.25em 0}
.message-markdown strong{font-weight:700}
.message-markdown pre{white-space:pre-wrap;overflow-wrap:anywhere;margin:.65em 0}
.message-markdown code{font-size:.95em}
.message-markdown blockquote{margin:.65em 0;padding-inline-start:1em}
.message-markdown a{text-decoration:underline;overflow-wrap:anywhere}
.message-markdown a:focus-visible{outline:3px solid currentColor;outline-offset:2px}
`;
